import { useCallback } from 'react';
import type { Lens } from '@hookform/lenses';
import { useFieldArray } from '@hookform/lenses/rhf';
import { z } from 'zod/v4';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { PlainIconButton } from '@interxfi/design-system/components/button';
import { Button } from '@interxfi/design-system/components/button/button';
import { Typography } from '@interxfi/design-system/components/typography';
import DeleteIcon from '@interxfi/design-system/icons/react/icon-delete';
import PlusIcon from '@interxfi/design-system/icons/react/icon-plus';
import type { IxmUserauthV1ContactDetails } from '@interxfi/types';
import { AppTextInputWithLens } from '../../../../../components/form/app-text-input';

export const programContactSchema = z.object({
  name: z.string().nonempty('Name is required'),
  title: z.string().nonempty('Title is required'),
  phone: z.string().nonempty('Phone number is required'),
  email: z.email({ error: 'A valid email address is required' }).nonempty('Email is required'),
});

export type ProgramContactFormValues = z.infer<typeof programContactSchema>;

export function getProgramContactGroupSchema(label: string) {
  return z.array(programContactSchema).min(1, `At least one ${label} contact is required`);
}

export type ProgramContactGroupFormValues = z.infer<ReturnType<typeof getProgramContactGroupSchema>>;

const defaultProgramContactFormValues: ProgramContactFormValues = {
  name: '',
  title: '',
  phone: '',
  email: '',
};

export const defaultProgramContactFormGroupValues: ProgramContactGroupFormValues = [{ ...defaultProgramContactFormValues }];

export function getInitialFormValuesForProgramContact(data: IxmUserauthV1ContactDetails | undefined): ProgramContactFormValues {
  if (!data) {
    return defaultProgramContactFormValues;
  }

  return {
    name: data.name || '',
    title: data.title || '',
    phone: data.phone || '',
    email: data.email || '',
  };
}

export function formValuesToProgramContact(values: ProgramContactFormValues): IxmUserauthV1ContactDetails {
  return {
    name: values.name,
    title: values.title,
    phone: values.phone,
    email: values.email,
  };
}

export function getInitialFormValuesForProgramContactGroup(data: IxmUserauthV1ContactDetails[] | undefined): ProgramContactGroupFormValues {
  if (!data || data.length === 0) {
    return defaultProgramContactFormGroupValues;
  }

  return data.map(getInitialFormValuesForProgramContact);
}

export function formValuesToProgramContactGroup(values: ProgramContactGroupFormValues): IxmUserauthV1ContactDetails[] | undefined {
  return values.map(formValuesToProgramContact);
}

interface ProgramContactGroupFormProps {
  appendLabel: string;
  label: string | undefined; // Explicitly undefined for optional wrappers
  lens: Lens<ProgramContactGroupFormValues>;
}

export function ProgramContactGroupForm({ appendLabel, label, lens }: ProgramContactGroupFormProps) {
  const { append, fields, remove } = useFieldArray(lens.interop());
  const handleAppend = useCallback(() => {
    append({ ...defaultProgramContactFormValues });
  }, [append]);

  return (
    <VerticalStack gap={4}>
      {label ? <Typography.Level3>{label}</Typography.Level3> : null}

      {lens.map(fields, (arrayField, l, i) => (
        <VerticalStack gap={4} key={arrayField.id}>
          <AppTextInputWithLens
            label="Name"
            lens={l.focus('name')}
            required
            suffix={
              i > 0 ? (
                <PlainIconButton
                  aria-label={`Remove contact at field number: ${i + 1}`}
                  onClick={() => {
                    remove(i);
                  }}
                  tone="danger-dk"
                >
                  <DeleteIcon aria-hidden height={16} />
                </PlainIconButton>
              ) : undefined
            }
          />
          <AppTextInputWithLens label="Job Title" lens={l.focus('title')} required />
          <AppTextInputWithLens autoComplete="off" label="Email Address" lens={l.focus('email')} required />
          <AppTextInputWithLens autoComplete="off" label="Phone" lens={l.focus('phone')} required />
        </VerticalStack>
      ))}

      <Button affixIcon={<PlusIcon aria-hidden />} onClick={handleAppend} size="small" variant="light">
        {appendLabel}
      </Button>
    </VerticalStack>
  );
}
